import { encodePeriodValue } from '@components'

import { ItemsPerPage, useItemsPerPage } from '@billy/design-system'
import { useMemo } from 'react'

import { BillStatus } from '@features/bills/enums/billStatus'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useGlobalFiscalYear } from '../../../../../../contexts/globalFiscalYearContext'
import { SortDirection } from '../../../../../../enums/sortDirection'
import { useQueryParams } from '../../../../../../hooks/useQueryParams'
import { getPeriodValueFromFiscalYear } from '../../../../../../utils/getPeriodValueFromFiscalYear'
import { BillsSortProperty } from '../../../../query-api'

export interface BillsListFilters {
  contactId?: string
  contactName?: string
  page?: number
  pageSize?: ItemsPerPage
  period?: string
  searchQuery?: string
  sortDirection?: SortDirection
  sortProperty?: BillsSortProperty
  status?: BillStatus
}

type UseBillsFiltersResponse = [BillsListFilters, (filters: Partial<BillsListFilters>) => void]

export const defaultQuery: BillsListFilters = {
  contactId: undefined,
  contactName: undefined,
  page: 1,
  pageSize: undefined,
  period: undefined,
  searchQuery: undefined,
  sortDirection: SortDirection.Desc,
  sortProperty: BillsSortProperty.EntryDate,
  status: BillStatus.All,
}

interface UseBillsListFiltersProps {
  enabled?: boolean
  sortDirection?: SortDirection
  sortProperty?: BillsSortProperty
  status?: BillStatus
}

// Query params are kept across navigation so shared filters (period, page, search, …) stay in
// sync between routes (when you navigate from one to another). `sortProperty` and `status` use the same keys with
// different values!, so a leftover from the previous view is used when bills can use the same
// value (invoices `dueDate`, transactions `voucherNo`, invoices `unpaid`, same api interface), and otherwise
// it is dropped (bills don't know what contact_name is, so we filter it out)
//
// When you jump between Ember and React, it is messier because of different implementations. So here this filtering
// is even more important.
//
// Examples of values that might be carried over to bills:
//   sortProperty  creditorbalance / debtorbalance  → contact_name, notOverdue, 16-30, 31+
//                 invoices                         → invoiceNo, issueDate, status
//                 contacts                         → name
//                 transactions                     → transactionNo, hasVoucher
//   status        bank sync                        → reconciled, unreconciled
//                 invoices                         → sent, unsent, credited, open, reminded
//                 quotes                           → closed, accepted, invoiced
// Example: going from creditor balance to Bills, we still want to keep selected date range and search queries we had in the creditor balance list to see the matching bills. So we pass query params through. But sorting _might_ not be valid for bills (entryDate is valid, contact_name is not), so we drop it or keep it.
const isBillsSortProperty = (value: unknown): value is BillsSortProperty =>
  (Object.values(BillsSortProperty) as unknown[]).includes(value)

const isBillStatus = (value: unknown): value is BillStatus => (Object.values(BillStatus) as unknown[]).includes(value)

export const useBillsListFilters = ({
  enabled = true,
  sortDirection,
  sortProperty,
  status,
}: UseBillsListFiltersProps = {}): UseBillsFiltersResponse => {
  const [pageSize] = useItemsPerPage()
  const { organization } = useUserOrganization()
  const { fiscalYear: globalFiscalYear } = useGlobalFiscalYear()
  const fallbackSortProperty = sortProperty || defaultQuery.sortProperty
  const fallbackStatus = status || defaultQuery.status
  const [queryParams, setQueryParams] = useQueryParams<BillsListFilters>({
    enabled,
    defaultQuery: {
      ...defaultQuery,
      sortDirection: sortDirection || defaultQuery.sortDirection,
      sortProperty: fallbackSortProperty,
      status: fallbackStatus,
      ...(globalFiscalYear && organization?.id
        ? { period: encodePeriodValue(getPeriodValueFromFiscalYear(globalFiscalYear), organization.id) }
        : {}),
      pageSize,
    },
  })

  return useMemo(
    () => [
      {
        ...queryParams,
        // Sometimes `page` comes as a string. It needs to be always `number` type.
        page: Number(queryParams.page),
        // Sometimes `pageSize` comes as a string. It needs to be always `number` type.
        pageSize: Number(queryParams.pageSize) as ItemsPerPage,
        sortProperty: isBillsSortProperty(queryParams.sortProperty) ? queryParams.sortProperty : fallbackSortProperty,
        status: isBillStatus(queryParams.status) ? queryParams.status : fallbackStatus,
      },
      setQueryParams,
    ],
    [fallbackSortProperty, fallbackStatus, queryParams, setQueryParams],
  )
}
